import { HiddenCanvas } from '../utils.js';
import { isIOS } from '../core/environment.js';
import { save } from './save.js';
import { beadCloud, browserFields } from '../core/azure.js';
import { libraries } from '../libraries.js';
import { patterns } from '../patterns/patterns.js';
import { utils } from '../utils.js';
import { migrateProject } from '../procedures/migrate.js';

export const project = {};

project.initialize = () => {
    b3.store.mySet('startedApp', true);
};

const readFileAsText = inputFile => {
    const myReader = new FileReader();

    return new Promise((resolve, reject) => {
        myReader.onerror = () => {
            myReader.abort();
            reject(new DOMException('Problem with input file.'));
        };

        myReader.onload = () => {
            resolve(myReader.result);
        };
        myReader.readAsText(inputFile);
    });
};

project.upload = () => {
    const files = document.querySelector('#load-project').files;

    const loop = async () => {
        for (let i = 0; i < files.length; i++) {

            const file = files[i];
            try {
                const fileContents = await readFileAsText(file);
                const saveObject = JSON.parse(fileContents);
                const recordObj = await migrateProject(saveObject);
                if (typeof recordObj === 'object') {
                    save.upload({recordObj});
                }
            } catch (e) {
                console.warn(e.message);
            }
        }
    };

    if (files) {
        loop();
        project.clearCache();
        project.doBrowserQuery();
    }
};

project.screenshot = () => {
    const canvas = document.getElementsByTagName('canvas')?.[0];

    if (!canvas) return;

    const hidden = new HiddenCanvas(game.scale.width-70, game.scale.height-60);

    hidden.ctx.drawImage(
        canvas,
        90,//Start Clipping
        60,//Start Clipping
        game.scale.width-90,//Clipping Width
        game.scale.height-60,//Clipping Height
        0,//Place X
        0,//Place Y
        hidden.canvas.width,//Place Width
        hidden.canvas.height//Place Height
    );

    const canvasToChoose = (isIOS || window.safari) ? canvas : hidden.canvas;

    canvasToChoose.toBlob(function (blob) {
        const myDownload = document.getElementById('my-download');
        myDownload.onclick = function (event) {
            let url = window.URL.createObjectURL(blob);
            this.href = url;
            this.target = '_blank';
            this.download = 'beadographer_image.png'
        }
        myDownload.click();
    }, 'image/png');

    hidden.remove();
};

project.deleteFile = async id => {

    await beadCloud({
        fn: 'deleteItemFromCloud',
        params: {id, container: 'projects'}
    });

    // remove from browser results
    const results = [...b3.store.state.browserResults];
    const entry = results.find(result => result.id === id);
    results.splice(results.indexOf(entry), 1)
    b3.store.mySet('browserResults', results);

    // to do: we can selectively clear the cache to reduce bandwidth
    project.clearCache();
};

project.cacheBrowserQuery = ({query, dbResult}) => {

    // put files in result cache
    b3.store.mySet('browserResultCache', {
        ...b3.store.state.browserResultCache,
        ...dbResult.resources.reduce((prev, curr) => ({...prev, [curr.id]: curr}), {})
    });

    // cache the query with a list of ids so we can retrieve the files later
    b3.store.state.browserQueryCache[JSON.stringify(query)] = dbResult.resources.map(file => file.id);
};

project.getCachedBrowserQuery = ({query}) => {

    const idList = b3.store.state.browserQueryCache[JSON.stringify(query)];
    if (!idList) return;

    // populate projects from results cache
    const result = {
        resources: idList.map(id => b3.store.state.browserResultCache[id])
    };

    return result;
};

project.updateResultCache = ({id, recordObj, fields=browserFields}) => {
    const fileToUpdate = b3.store.state.browserResultCache[id];
    if (!fileToUpdate) return;
    fields.forEach(key => {
        fileToUpdate[key] = recordObj[key];
    });
};

project.cacheCount = ({query, count}) => {
    b3.store.state.browserQueryCountCache[JSON.stringify(query)] = count;
};

project.getCachedCount = ({query}) => {
    const count = b3.store.state.browserQueryCountCache[JSON.stringify(query)];
    return count;
};

project.clearCache = () => {
    b3.store.mySet('browserQueryCountCache', {});
    b3.store.mySet('browserQueryCache', {});
};

project.doBrowserQuery = async ({
    orderBy='dateModified',
    offset=0,
    limit=10,
    sortDirection='DESC',
    patternFilter='All patterns',
    libraryFilter='All libraries',
    tagFilter='',
} = {}) => {
    if (!loggedIn) return;

    libraryFilter = libraryFilter === 'All libraries' ?
        'All' :
        libraries.find(library => library.displayName === libraryFilter).index;

    patternFilter = patternFilter === 'All patterns' ?
        'All' :
        patterns.arr.find(p => p.displayName === patternFilter).name;

    const query = {orderBy, offset, limit, sortDirection, patternFilter, libraryFilter, tagFilter, currentUserId};

    // get from cache
    const cachedCount = project.getCachedCount({query: {patternFilter, libraryFilter, tagFilter}});
    const cachedResult = project.getCachedBrowserQuery({query});

    // get from db if no cached result
    const dbResult = cachedResult || await beadCloud({
        fn: 'browserQuery',
        params: query
    }) || {};
    const { count } = cachedCount ?
        { count: cachedCount } :
        await beadCloud({
            fn: 'browserQueryCount',
            params: query
        });

    // add to cache
    if (Array.isArray(dbResult.resources)) {
        if (!cachedResult) project.cacheBrowserQuery({query, dbResult});
    }
    if (!cachedCount) project.cacheCount({query: {patternFilter, libraryFilter, tagFilter}, count});

    // set browser results
    if (Array.isArray(dbResult.resources)) {
        const myResults = dbResult.resources.map(file => ({
            ...file,
            sizeDescription: utils.getSizeDescription(file),
            libraryDisplayName: libraries[file.settings.libraryIndex].displayName,
            patternDisplayName: patterns.arr.find(p => p.name === file.settings.patternName)?.displayName || '',
        }));

        b3.store.mySet('browserResults', myResults);
    }

    if (typeof count === 'number') {
        b3.store.mySet('browserResultCount', count);
    }

    b3.ee.emit('resultsLoaded');
};
